import {
  INTERNAL_DEFAULT_ROLE_NAMES,
  INTERNAL_PROTECTED_ROLE_NAMES,
  InternalRoleType,
} from 'src/constants/internal-access/internalRoles'
import type {
  InternalAccessRole,
  InternalRoleEditability,
  InternalRoleFormValues,
  InternalRoleUpdateBody,
} from 'src/interfaces/internalAccess'

const VIEW_ONLY_REASON = 'You have view-only access to roles.'
const SYSTEM_ROLE_REASON = "System role — managed by the platform and can't be edited."
const NOTHING_TO_EDIT_REASON =
  "This built-in role grants every permission and can't be renamed, so there is nothing to edit."
const NOTHING_TO_EDIT_UNHELD_REASON =
  "You can't rename this built-in role and it includes permissions you don't hold, so there is nothing to edit."
const UNHELD_PERMISSIONS_HELPER =
  "This role includes permissions you don't hold, so its permission list can't be changed."
const BUILT_IN_NAME_HELPER = "Built-in roles are referenced by name and can't be renamed."

/** Mirrors the backend's `isProtected`: the `isSystem` column OR the protected-name list. */
export const isProtectedInternalRole = (
  role: Pick<InternalAccessRole, 'name' | 'isSystem'>,
): boolean => {
  return role.isSystem || INTERNAL_PROTECTED_ROLE_NAMES.includes(role.name)
}

export const isBuiltInInternalRoleName = (name: string): boolean => {
  return INTERNAL_DEFAULT_ROLE_NAMES.includes(name)
}

export const resolveInternalRoleType = (
  role: Pick<InternalAccessRole, 'name' | 'isSystem'>,
): InternalRoleType => {
  if (isProtectedInternalRole(role)) {
    return InternalRoleType.System
  }

  if (isBuiltInInternalRoleName(role.name)) {
    return InternalRoleType.BuiltIn
  }

  return InternalRoleType.Custom
}

const resolveNothingToEditReason = (hasUnheldPermissions: boolean): string => {
  return hasUnheldPermissions ? NOTHING_TO_EDIT_UNHELD_REASON : NOTHING_TO_EDIT_REASON
}

/**
 * Locks only what the backend would refuse (see the spec's §2.1 table). The backend still decides;
 * this keeps the editor from offering a change that cannot succeed.
 */
export const resolveRoleEditability = ({
  role,
  canManage,
  grantable,
}: {
  role: InternalAccessRole | null
  canManage: boolean
  /** The editor's own permissions: the backend rejects a list containing any they don't hold. */
  grantable: string[]
}): InternalRoleEditability => {
  if (!canManage) {
    return {
      canEditName: false,
      canEditPermissions: false,
      canSave: false,
      lockReason: VIEW_ONLY_REASON,
      nameHelperText: null,
      permissionsHelperText: null,
    }
  }

  if (!role) {
    return {
      canEditName: true,
      canEditPermissions: true,
      canSave: true,
      lockReason: null,
      nameHelperText: null,
      permissionsHelperText: null,
    }
  }

  if (isProtectedInternalRole(role)) {
    return {
      canEditName: false,
      canEditPermissions: false,
      canSave: false,
      lockReason: SYSTEM_ROLE_REASON,
      nameHelperText: null,
      permissionsHelperText: null,
    }
  }

  const canEditName = !isBuiltInInternalRoleName(role.name)
  // The backend checks the WHOLE submitted list against the editor's permissions, so one
  // permission the editor lacks makes every permission save on this role fail.
  const hasUnheldPermissions =
    !role.allPermissions &&
    role.permissions.some((permission) => {
      return !grantable.includes(permission)
    })
  const canEditPermissions = !role.allPermissions && !hasUnheldPermissions
  const canSave = canEditName || canEditPermissions

  return {
    canEditName,
    canEditPermissions,
    canSave,
    lockReason: canSave ? null : resolveNothingToEditReason(hasUnheldPermissions),
    nameHelperText: canEditName ? null : BUILT_IN_NAME_HELPER,
    permissionsHelperText: hasUnheldPermissions ? UNHELD_PERMISSIONS_HELPER : null,
  }
}

export const arePermissionSetsEqual = (left: string[], right: string[]): boolean => {
  const leftSet = new Set(left)
  const rightSet = new Set(right)

  if (leftSet.size !== rightSet.size) {
    return false
  }

  return Array.from(leftSet).every((permission) => {
    return rightSet.has(permission)
  })
}

/**
 * The PATCH body: only the keys that changed, or `null` when nothing did (the backend refuses an
 * empty update, and re-checks every permission in a submitted list against the editor's own).
 */
export const buildRoleUpdatePayload = (
  role: InternalAccessRole,
  values: InternalRoleFormValues,
): InternalRoleUpdateBody | null => {
  const body: InternalRoleUpdateBody = {}
  const trimmedName = values.name.trim()

  if (trimmedName !== role.name) {
    body.name = trimmedName
  }

  if (!role.allPermissions && !arePermissionSetsEqual(role.permissions, values.permissions)) {
    body.permissions = Array.from(new Set(values.permissions))
  }

  if (body.name === undefined && body.permissions === undefined) {
    return null
  }

  return body
}
